import { useCallback, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import type { CellValueChangedEvent } from 'ag-grid-community/dist/types/core/events';
import { BudgetSiSClassification } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import TableGridItem from '../UI/TableGridItem';
import {
    useWorkingUnitClassifications,
    useUpdateWorkingUnitClassification,
} from '../../hooks/api/budgetWorkingUnitClassification';
import { useBudgetContext } from '../../store/Budget/BudgetContext';
import LoadError from './LoadError';

const SIS_OPTIONS = [
    BudgetSiSClassification.SERVICE,
    BudgetSiSClassification.SALES,
    BudgetSiSClassification.SIS,
    BudgetSiSClassification.RETAIN,
];

const WorkingUnitMappingGrid = () => {
    const gridRef = useRef<AgGridReact>(null);
    const { t } = useTranslation('budget');
    // CALL-745: Working Unit classification is global master data; editable for budget users.
    const { canEdit } = useBudgetContext();
    const [classifications, isLoading, { isError, error, refetch }] = useWorkingUnitClassifications();
    const { mutate: updateClassification } = useUpdateWorkingUnitClassification();

    const rowData = useMemo(
        () =>
            classifications.map((wu) => ({
                id: wu.id,
                workingUnit: [wu.worktype?.worktype, wu.channel?.name, wu.task?.name].filter(Boolean).join(' - '),
                worktype: wu.worktype?.worktype ?? '',
                channel: wu.channel?.name ?? '',
                task: wu.task?.name ?? '',
                budgetSiSClassification: wu.budgetSiSClassification ?? '',
            })),
        [classifications]
    );

    const columnDefs: ColDef[] = useMemo(
        () => [
            { headerName: t('field.id'), field: 'id', minWidth: 70, maxWidth: 90, editable: false },
            {
                headerName: t('field.workingUnit'),
                field: 'workingUnit',
                minWidth: 240,
                flex: 1.2,
                hide: true,
                editable: false,
            },
            { headerName: t('field.worktype'), field: 'worktype', minWidth: 150, flex: 1, editable: false },
            { headerName: t('field.channel'), field: 'channel', minWidth: 130, flex: 1, editable: false },
            { headerName: t('field.task'), field: 'task', minWidth: 150, flex: 1, editable: false },
            {
                headerName: t('field.sisServiceSales'),
                field: 'budgetSiSClassification',
                minWidth: 160,
                editable: canEdit,
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: {
                    values: ['', ...SIS_OPTIONS],
                    cellHeight: 30,
                    formatValue: (value: string) => value || t('common.noneParen'),
                },
                ...(canEdit ? { cellStyle: { backgroundColor: '#fffde7' } } : {}),
            },
        ],
        [canEdit, t]
    );

    const handleCellValueChanged = useCallback(
        (event: CellValueChangedEvent) => {
            const { data, colDef } = event;
            if (!data?.id || colDef.field !== 'budgetSiSClassification') return;

            updateClassification({
                id: data.id,
                budgetSiSClassification: data.budgetSiSClassification || null,
            });
        },
        [updateClassification]
    );

    const defaultColDef: ColDef = useMemo(
        () => ({
            sortable: true,
            filter: true,
            resizable: true,
        }),
        []
    );

    if (isError) return <LoadError error={error} onRetry={refetch} />;

    return (
        <div className="budget-grid">
            <TableGridItem
                ref={gridRef}
                height={'auto'}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onCellValueChanged={handleCellValueChanged}
                getRowId={(params) => String(params.data.id)}
                loading={isLoading}
                cellSelection
                pagination
                paginationPageSize={20}
                paginationPageSizeSelector={[10, 20, 50, 100]}
                noRowsOverlayComponent={() => <span style={{ padding: 10 }}>{t('hint.noData')}</span>}
            />
        </div>
    );
};

export default WorkingUnitMappingGrid;
